import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Observable } from 'rxjs';
import { ImageDto } from 'src/app/models/image-dto';
import { LicenseDto } from 'src/app/models/license-dto';
import { MultiLangStrDto } from 'src/app/models/multi-lang-str-dto';
import { PlasterSampleDto } from 'src/app/models/plaster-sample-dto';

@Component({
  selector: 'plaster-sample-details',
  templateUrl: './plaster-sample-details.component.html',
  styleUrls: ['./plaster-sample-details.component.scss']
})
export class PlasterSampleDetailsComponent implements OnChanges {
  @Input() selectedSample!: PlasterSampleDto;
  @Input() currentLanguage!: Observable<string>;
  @Output() navigate = new EventEmitter<{ entityId: number, entityType: string }>();

  samplePhoto: ImageDto[] = [];
  microscopePhoto: ImageDto[] = [];
  notes: { labelKey: string, value: MultiLangStrDto }[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedSample']?.currentValue) {
      this.samplePhoto = this.selectedSample?.samplePhoto ? [this.selectedSample.samplePhoto] : [];
      this.microscopePhoto = this.selectedSample?.microscopePhoto ? [this.selectedSample.microscopePhoto] : [];
      this.notes = this.buildNotes();
    }
  }

  // The sample's notes that have text, in display order.
  private buildNotes(): { labelKey: string, value: MultiLangStrDto }[] {
    const candidates = [
      { labelKey: 'iaa.app.plasterSample.notes', value: this.selectedSample?.notes },
      { labelKey: 'iaa.app.plasterSample.compositionNotes', value: this.selectedSample?.compositionNotes },
      { labelKey: 'iaa.app.plasterSample.layersNotes', value: this.selectedSample?.layersNotes },
    ];
    return candidates.filter((n): n is { labelKey: string, value: MultiLangStrDto } =>
      !!n.value?.hebrew || !!n.value?.english);
  }

  // Past this many declared sites the list folds into a panel, or it crowds out the other details.
  readonly maxInlineSites = 3;

  get layersCount(): number {
    return this.selectedSample?.layers?.length || 0;
  }

  get sitesCount(): number {
    return this.selectedSample?.hachrazot?.length || 0;
  }

  // The source names a licence however it likes (often the short name), but the
  // relation is resolved to the licence entity on import, so we always have both.
  // Show the long name - same as the results list - and fall back only if missing.
  getLicenseNames(licenses: LicenseDto[] | undefined): string {
    return licenses ? licenses.map(l => l.longName || l.shortName).join(', ') : '';
  }

  // layerType is the layer's position, worked out by the crawler from the layer count
  // (single / top / middle / bottom). Past 3 layers it writes layer4, layer5... which
  // aren't positions, so those get no key and the cell stays empty.
  getLayerPositionKey(layerType: string | null): string | null {
    return layerType && ['single', 'top', 'middle', 'bottom'].includes(layerType)
      ? `iaa.app.plasterSample.layerPositions.${layerType}`
      : null;
  }

  // The compositions of a layer read as one cell: "סיד, חרסית".
  getCompositions(compositions: string[] | undefined): string {
    return compositions?.length ? compositions.join(', ') : '';
  }

  // "Microscope photo – <sample title>", so the regular and microscope photos still read differently.
  photoAltName(photoLabel: string, sampleTitle: string | null | undefined): string {
    return sampleTitle ? `${photoLabel} – ${sampleTitle}` : photoLabel;
  }

  goTo(entityId: number, entityType: string): void {
    this.navigate.emit({ entityId, entityType });
  }
}
